import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { CardContainer, ErrorMessage, InfoMessage } from "@msys/ui";
import {
  OpenInNew as OpenInNewIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import {
  Button,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import React from "react";
import { CardSaveRow, FormDirtyTracker } from "./integrationCardForm.js";
import {
  hasBrokenStoredAssignment,
  NO_LETTERHEAD,
  offersSaveWhilePristine,
  ORGANISATION_DEFAULT,
  storedAssignmentTemplateIds,
  templateAssignmentInitialValues,
  toTemplateLetterheadsSlice,
  type TemplateLetterheadEntry,
  type TemplateLetterheadValues,
} from "./pdfGeneratorSettingsAssignments.js";
import { usePdfGeneratorSettings_TemplateLetterheadsQuery } from "./TemplateLetterheadsCard.generated.js";

/**
 * Its own file rather than a section of `PdfGeneratorSettings`, because that
 * one builds the dashboard URL through the `environment` module, which reads
 * `import.meta.env` at load and is therefore unloadable under Jest. This card
 * owns the guard that stands between a failed template read and a persisted
 * empty assignment list, which is exactly the thing worth a component test —
 * so it takes the URL as a prop and keeps the environment lookup in the page
 * above it.
 */
export function TemplateLetterheadsCard({
  onSave,
  onDirtyChange,
  storedSettings,
  templatesDashboardUrl,
}: {
  onSave: (assignments: TemplateLetterheadEntry[]) => Promise<void>;
  onDirtyChange: (dirty: boolean) => void;
  /** The integration's raw settings blob, the only view of assignments whose template no longer exists. */
  storedSettings: unknown;
  templatesDashboardUrl: string;
}) {
  const { t } = useTranslate("Integrations");
  const client = useApolloClient();
  const query = usePdfGeneratorSettings_TemplateLetterheadsQuery({ client });

  const templatesResult = getDataOrNull(
    query.data?.organisationPdfGeneratorTemplates
  );
  const templates = templatesResult?.templates ?? [];
  const letterheads =
    (query.data?.viewer?.__typename === "Viewer"
      ? query.data.viewer.organisation.pdfLetterheads
      : undefined) ?? [];

  // Saving builds the assignment list from the rendered rows, which prunes
  // templates the external service no longer reports. That is only sound when
  // the read succeeded: submitting rows we never received would delete every
  // assignment the organisation has. So a failed read renders no form at all,
  // and therefore cannot submit one.
  //
  // A successful read of an *empty* list is not that case, and the two are told
  // apart deliberately: reporting it as a failure sends an administrator looking
  // for an outage that is not happening, and it legitimately persists the empty
  // set, because every assignment left is for a template nothing reports.
  const couldNotReadTemplates =
    Boolean(query.error) || !templatesResult?.integrationEnabled;
  const hasNoTemplates = !couldNotReadTemplates && templates.length === 0;

  const initialValues = templateAssignmentInitialValues(templates, letterheads);
  // Saving can differ from what the rows show without anyone editing them —
  // pruning a broken assignment, or dropping one whose template the service no
  // longer reports and which therefore has no row at all. Dirtiness cannot see
  // either, so the save row is offered explicitly. The second needs the stored
  // ids, which only the settings blob carries.
  const offerSaveWhilePristine =
    !couldNotReadTemplates &&
    offersSaveWhilePristine(
      templates,
      letterheads,
      storedAssignmentTemplateIds(storedSettings)
    );

  return (
    <CardContainer
      title={t("Letterheads per template")}
      // In the header, not the body: the templates live in another service, so
      // the way out to them must stay reachable in the states the body replaces
      // with an error — which are exactly the states someone would leave to fix.
      ActionButton={
        <Button
          variant="outlined"
          size="extra-small"
          endIcon={<OpenInNewIcon />}
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          href={templatesDashboardUrl}
          // `CardContainer` wraps this slot in a Stack that calls
          // `preventDefault()` on the bubbled click, to stop an action button
          // from also toggling the card. On a link that cancels the navigation
          // itself, so the click has to stop before it reaches that handler.
          // Kept a real anchor rather than a `window.open` click, so
          // middle-click and "open in new tab" keep working.
          onClick={event => event.stopPropagation()}
        >
          {t("Go to templates dashboard")}
        </Button>
      }
    >
      <Stack spacing={1.5} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t(
            "A template with no letterhead assigned is generated without one, using its own layout from the PDF dashboard."
          )}{" "}
          {t("Templates are managed in the PDF dashboard.")}
        </Typography>

        {/*
         * The whole loading state, not just the one with nothing cached: the
         * client's default policy is cache-and-network, so a warm cache renders
         * templates while the read is still in flight, and saving from that
         * list would prune assignments made since it was cached. The refetch
         * after save does not reintroduce this state — `loading` follows the
         * network only with `notifyOnNetworkStatusChange`, which is not set.
         */}
        {query.loading ? null : couldNotReadTemplates ? (
          <ErrorMessage
            message={t(
              "The template list could not be loaded, so assignments cannot be edited right now."
            )}
          />
        ) : (
          <Formik<TemplateLetterheadValues>
            initialValues={initialValues}
            enableReinitialize
            onSubmit={async values => {
              await onSave(toTemplateLetterheadsSlice(values));
              // This card's own query, which the page's refetch does not cover:
              // the assignments hang off the templates query, and the update
              // mutation returns the integration rather than the templates, so
              // nothing else repoints them. Without this the row values stay at
              // the pre-save ids, leaving `enableReinitialize` to restore stale
              // initial values — the card would read dirty straight after a
              // successful save and arm the unsaved-changes guard.
              await query.refetch();
            }}
          >
            {fp => (
              <Form>
                <FormDirtyTracker
                  dirty={fp.dirty}
                  onDirtyChange={onDirtyChange}
                />
                <Stack spacing={1.5}>
                  {/*
                   * A successful read of an empty list, which is not the failed
                   * read above: saving still persists the empty set, because
                   * every assignment left is for a template nothing reports.
                   */}
                  {hasNoTemplates && (
                    <InfoMessage
                      message={t(
                        "No PDF templates exist yet. Create one in the templates dashboard, then assign it a letterhead here."
                      )}
                    />
                  )}
                  {templates.map((template, index) => (
                    <TemplateRow
                      key={template.id}
                      name={template.name}
                      letterheads={letterheads}
                      isBroken={hasBrokenStoredAssignment(
                        template,
                        letterheads
                      )}
                      value={
                        fp.values.assignments[index]?.selection ?? NO_LETTERHEAD
                      }
                      onChange={selection =>
                        fp.setFieldValue(
                          `assignments.${index}.selection`,
                          selection
                        )
                      }
                    />
                  ))}
                  <CardSaveRow
                    fp={fp}
                    offerWhilePristine={offerSaveWhilePristine}
                  />
                </Stack>
              </Form>
            )}
          </Formik>
        )}
      </Stack>
    </CardContainer>
  );
}

function TemplateRow({
  name,
  letterheads,
  isBroken: hasBrokenStoredAssignment,
  value,
  onChange,
}: {
  name: string;
  letterheads: Array<{ id: string; name: string; isDefault: boolean }>;
  /** The *stored* assignment names a letterhead that is gone. */
  isBroken: boolean;
  value: string;
  onChange: (selection: string) => void;
}) {
  const { t } = useTranslate("Integrations");

  // An assignment naming a letterhead that is no longer in the list is BROKEN,
  // not unassigned: generation refuses such a template rather than composing
  // something else. This card belongs to the only person who can fix it, so it
  // must not look like the harmless case. Saving prunes it; save is not blocked
  // on it, because an administrator here to fix another row should not be held
  // hostage by this one.
  //
  // The warning tracks the stored assignment, while the value shown is already
  // the pruned one — so picking another setting clears the warning, and
  // reverting to "no letterhead" brings it back, which is honest: the stale
  // entry is still stored until this form is saved.
  const isBroken = hasBrokenStoredAssignment && value === NO_LETTERHEAD;

  // One string, shown twice — as the field's helper text and as the icon's
  // tooltip — so the warning is legible whether or not the row is in view.
  const brokenMessage = t(
    "The assigned letterhead no longer exists. This template cannot generate a PDF until another is assigned."
  );

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <TextField
        select
        size="small"
        fullWidth
        label={name}
        value={value}
        onChange={event => onChange(event.target.value)}
        error={isBroken}
        helperText={isBroken ? brokenMessage : undefined}
        // "No letterhead" is the empty value, which a select otherwise renders
        // as a blank field under an unshrunk label.
        slotProps={{
          select: { displayEmpty: true },
          inputLabel: { shrink: true },
        }}
      >
        <MenuItem value={NO_LETTERHEAD}>{t("No letterhead")}</MenuItem>
        <MenuItem value={ORGANISATION_DEFAULT}>
          {t("Organisation's default letterhead")}
        </MenuItem>
        {letterheads.map(letterhead => (
          <MenuItem key={letterhead.id} value={letterhead.id}>
            {letterhead.name}
          </MenuItem>
        ))}
      </TextField>
      {isBroken && (
        <Tooltip title={brokenMessage}>
          <WarningIcon color="error" fontSize="small" />
        </Tooltip>
      )}
    </Stack>
  );
}
